type Listener = (online: boolean) => void;

class ConnectivityService {
  private listeners = new Set<Listener>();
  private _online = typeof navigator !== 'undefined' ? navigator.onLine : true;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  private handleOnline = () => {
    this._online = true;
    this.emit();
  };

  private handleOffline = () => {
    this._online = false;
    this.emit();
  };

  private emit() {
    for (const l of this.listeners) {
      try {
        l(this._online);
      } catch {
        /* ignore listener errors */
      }
    }
  }

  get online(): boolean {
    return this._online;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Active probe: ping /health to confirm reachability rather than
   * relying solely on `navigator.onLine` (which lies on captive portals).
   * Caller may use this on app boot or before a sync attempt.
   */
  async probe(probeFn: () => Promise<unknown>): Promise<boolean> {
    try {
      await probeFn();
      if (!this._online) {
        this._online = true;
        this.emit();
      }
      return true;
    } catch {
      if (this._online) {
        this._online = false;
        this.emit();
      }
      return false;
    }
  }
}

export const connectivity = new ConnectivityService();
